import nextFrame from 'next-frame';

import type {CoreOrB2b} from '../../utils/coreOrB2b/types';
import type {OmitRecursively} from '../../utils/utility-types';
import type {BNCoachGoalsBoundCommentReachedParams} from '../types/bnrks/methods';
import type {
  CoreClientGoalsMarkReachedParams,
  CoreClientGoalsReadParams,
  CoreClientSubgoalsMarkReachedParams,
  CoreClientSubgoalsOwnCreateParams,
  CoreClientSubgoalsOwnDeleteParams,
  CoreClientSubgoalsOwnUpdateParams,
  CoreClientSubgoalsReadParams,
  CoreClientSubgoalsUpdateParams,
  CoreCoachGoalsBoundCommentReachedParams,
  CoreCoachGoalsBoundDeleteParams,
  CoreCoachGoalsBoundReadParams,
  CoreCoachGoalsBoundSynchronizeParams,
  CoreCoachGoalsBoundUpdateParams,
  CoreCoachGoalsUnboundCreateParams,
  CoreCoachGoalsUnboundDeleteParams,
  CoreCoachGoalsUnboundReadParams,
  CoreCoachGoalsUnboundUpdateParams,
  CoreCoachSubgoalsBoundCommentReachedParams,
  CoreCoachSubgoalsBoundDeleteParams,
  CoreCoachSubgoalsBoundReadParams,
  CoreCoachSubgoalsBoundSynchronizeParams,
  CoreCoachSubgoalsBoundUpdateParams,
  CoreCoachSubgoalsUnboundCreateParams,
  CoreCoachSubgoalsUnboundDeleteParams,
  CoreCoachSubgoalsUnboundReadParams,
  CoreCoachSubgoalsUnboundUpdateParams,
} from '../types/core/methods';
import type {GoalApiModel, SubgoalApiModel} from './types';

import {pickCoreOrB2b} from '../../utils/coreOrB2b/pickCoreOrB2b';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';
import {expandObj} from '../utils/expand';

class ClientGoalStore<
  T extends GoalApiModel = GoalApiModel,
> extends EntityStore<T, CoreClientGoalsReadParams> {
  markReached = this._createMethod<CoreClientGoalsMarkReachedParams>({
    method: 'client.goals.mark_reached',
  });
  markReachedBatch = this._createBatchMethod<CoreClientGoalsMarkReachedParams>({
    method: 'client.goals.mark_reached',
  });
}

class ClientOwnSubgoalStore<
  T extends SubgoalApiModel = SubgoalApiModel,
> extends EntityStore<
  T,
  object,
  CoreClientSubgoalsOwnCreateParams,
  CoreClientSubgoalsOwnUpdateParams,
  CoreClientSubgoalsOwnDeleteParams
> {}

class ClientSubgoalStore<
  T extends SubgoalApiModel = SubgoalApiModel,
> extends EntityStore<
  T,
  CoreClientSubgoalsReadParams,
  object,
  CoreClientSubgoalsUpdateParams
> {
  markReached = this._createMethod<CoreClientSubgoalsMarkReachedParams>({
    method: 'client.subgoals.mark_reached',
  });
  markReachedBatch =
    this._createBatchMethod<CoreClientSubgoalsMarkReachedParams>({
      method: 'client.subgoals.mark_reached',
    });
  own = new ClientOwnSubgoalStore('subgoal', {
    create: 'client.subgoals.own.create',
    delete: 'client.subgoals.own.delete',
    update: 'client.subgoals.own.update',
  });
}

class CoachBoundGoalStore<
  T extends GoalApiModel = GoalApiModel,
> extends EntityStore<
  T,
  CoreCoachGoalsBoundReadParams,
  object,
  CoreCoachGoalsBoundUpdateParams,
  CoreCoachGoalsBoundDeleteParams
> {
  private internalCommentReached = this._createMethod<
    | BNCoachGoalsBoundCommentReachedParams
    | CoreCoachGoalsBoundCommentReachedParams
  >({
    method: 'coach.goals.bound.comment_reached',
  });
  private internalCommentReachedBatch =
    this._createBatchMethod<CoreCoachGoalsBoundCommentReachedParams>({
      method: 'coach.goals.bound.comment_reached',
    });

  commentReached(
    goal: T,
    {
      b2b,
      core,
    }: OmitRecursively<
      CoreOrB2b<
        CoreCoachGoalsBoundCommentReachedParams,
        BNCoachGoalsBoundCommentReachedParams
      >,
      '_id'
    > = {},
  ) {
    const params = pickCoreOrB2b<
      Omit<CoreCoachGoalsBoundCommentReachedParams, '_id'>,
      Omit<BNCoachGoalsBoundCommentReachedParams, '_id'>
    >({
      b2b,
      core,
    });

    return this.internalCommentReached(goal, params);
  }

  commentReachedBatch(
    args: {
      entity: T;
      params?: OmitRecursively<
        CoreOrB2b<
          CoreCoachGoalsBoundCommentReachedParams,
          BNCoachGoalsBoundCommentReachedParams
        >,
        '_id'
      >;
    }[],
  ) {
    return this.internalCommentReachedBatch(
      args.map(({entity, params}) => {
        const _params = pickCoreOrB2b<
          Omit<CoreCoachGoalsBoundCommentReachedParams, '_id'>,
          Omit<BNCoachGoalsBoundCommentReachedParams, '_id'>
        >({
          b2b: params?.b2b,
          core: params?.core,
        });

        return {
          entity,
          params: _params,
        };
      }),
    );
  }

  async synchronize(params: CoreCoachGoalsBoundSynchronizeParams) {
    const result = await apiRequest<CoreCoachGoalsBoundSynchronizeParams>({
      method: 'coach.goals.bound.synchronize',
      params,
    });

    const _goals: T[] = [];

    const goalsLength = result._items.length;

    for (let i = 0; i < goalsLength; i += 1) {
      await nextFrame();

      let item: T;

      if (params.expand) {
        item = expandObj<T>(result._items[i], {
          expand: params.expand,
          expanded: result._expanded,
        });
      } else {
        item = result._items[i];
      }

      _goals[i] = item;
    }

    return _goals;
  }
}

class CoachBoundSubgoalStore<
  T extends SubgoalApiModel = SubgoalApiModel,
> extends EntityStore<
  T,
  CoreCoachSubgoalsBoundReadParams,
  object,
  CoreCoachSubgoalsBoundUpdateParams,
  CoreCoachSubgoalsBoundDeleteParams
> {
  commentReached =
    this._createMethod<CoreCoachSubgoalsBoundCommentReachedParams>({
      method: 'coach.subgoals.bound.comment_reached',
    });
  commentReachedBatch =
    this._createBatchMethod<CoreCoachSubgoalsBoundCommentReachedParams>({
      method: 'coach.subgoals.bound.comment_reached',
    });

  async synchronize(params: CoreCoachSubgoalsBoundSynchronizeParams) {
    const result = await apiRequest<CoreCoachSubgoalsBoundSynchronizeParams>({
      method: 'coach.subgoals.bound.synchronize',
      params,
    });

    const _goals: T[] = [];

    const goalsLength = result._items.length;

    for (let i = 0; i < goalsLength; i += 1) {
      await nextFrame();

      let item: T;

      if (params.expand) {
        item = expandObj<T>(result._items[i], {
          expand: params.expand,
          expanded: result._expanded,
        });
      } else {
        item = result._items[i];
      }

      _goals[i] = item;
    }

    return _goals;
  }
}

class CoachGoalStore<T extends GoalApiModel = GoalApiModel> extends EntityStore<
  T,
  CoreCoachGoalsUnboundReadParams
> {
  bound = new CoachBoundGoalStore<T>('goal', {
    delete: 'coach.goals.bound.delete',
    fetch: 'coach.goals.bound.read',
    update: 'coach.goals.bound.update',
  });
}

class CoachSubgoalStore<
  T extends SubgoalApiModel = SubgoalApiModel,
> extends EntityStore<T, CoreCoachSubgoalsUnboundReadParams> {
  bound = new CoachBoundSubgoalStore<T>('subgoal', {
    delete: 'coach.subgoals.bound.delete',
    fetch: 'coach.subgoals.bound.read',
    update: 'coach.subgoals.bound.update',
  });
}

export class SubgoalStore<
  T extends SubgoalApiModel = SubgoalApiModel,
> extends EntityStore<
  T,
  object,
  CoreCoachSubgoalsUnboundCreateParams,
  CoreCoachSubgoalsUnboundUpdateParams,
  CoreCoachSubgoalsUnboundDeleteParams
> {
  client = new ClientSubgoalStore<T>('subgoal', {
    fetch: 'client.subgoals.read',
    update: 'client.subgoals.update',
  });
  coach = new CoachSubgoalStore<T>('subgoal', {
    fetch: 'coach.subgoals.unbound.read',
  });
}

export class GoalStore<
  T extends GoalApiModel = GoalApiModel,
> extends EntityStore<
  T,
  object,
  CoreCoachGoalsUnboundCreateParams,
  CoreCoachGoalsUnboundUpdateParams,
  CoreCoachGoalsUnboundDeleteParams
> {
  client = new ClientGoalStore<T>('goal', {
    fetch: 'client.goals.read',
  });
  coach = new CoachGoalStore<T>('goal', {
    fetch: 'coach.goals.unbound.read',
  });
  sub = new SubgoalStore('subgoal', {
    create: 'coach.subgoals.unbound.create',
    delete: 'coach.subgoals.unbound.delete',
    update: 'coach.subgoals.unbound.update',
  });
}

export const goalStore = new GoalStore('goal', {
  create: 'coach.goals.unbound.create',
  delete: 'coach.goals.unbound.delete',
  update: 'coach.goals.unbound.update',
});
